import { useSettingsContext } from '../theme/settings-drawer';
import { ReemaIconComponent } from '../display/Svg/ReemaIconComponent';
import { ReemaLogoComponent } from '../display/Svg/ReemaLogoComponent';
import { useDisplayModeContext } from '../theme/display';
import { Box, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import {
  COMPACT_GRID_WIDTH,
  EXPANDED_GRID_WIDTH,
} from '@act/react-components/app/constants';

interface AppHeaderLogoMarkProps {
  disableAnimations?: boolean;
}

/**
 * Reema header logo without a link wrapper. Use when the logo must not be
 * focusable or activatable (e.g. consent enrollment error).
 */
export const AppHeaderLogoMark = ({
  disableAnimations = false,
}: AppHeaderLogoMarkProps) => {
  const theme = useTheme();
  const { displayMode } = useDisplayModeContext();
  const isCompact = displayMode === 'compact';
  const { logoGradientColors } = useSettingsContext();
  const brandColor = theme.palette.primaryAccent.main;

  const compactWidth = parseFloat(COMPACT_GRID_WIDTH);
  const expandedWidth = parseFloat(EXPANDED_GRID_WIDTH);
  const width = isCompact ? compactWidth : expandedWidth;

  const logoLayers = (
    <>
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ReemaIconComponent
          color={brandColor}
          gradientColors={logoGradientColors}
          useGradient={logoGradientColors.length > 0}
          width="32px"
          height="auto"
          opacity={isCompact ? 1 : 0}
          transition={disableAnimations ? 'none' : 'opacity 0.3s 0.15s'}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ReemaLogoComponent
          gradientColors={logoGradientColors}
          useGradient={logoGradientColors.length > 0}
          sx={{
            color: brandColor,
            width: '136px',
            height: 'auto',
            verticalAlign: 'middle',
            opacity: !isCompact ? 1 : 0,
            transition: disableAnimations ? 'none' : 'opacity 0.3s 0.15s',
          }}
        />
      </Box>
    </>
  );

  const sharedSx = {
    height: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  } as const;

  if (disableAnimations) {
    return (
      <Box
        role="img"
        aria-label="Reema"
        sx={{
          ...sharedSx,
          width,
        }}
      >
        {logoLayers}
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      role="img"
      aria-label="Reema"
      animate={{
        width: isCompact ? compactWidth : expandedWidth,
      }}
      transition={{ type: 'tween', duration: 0.3 }}
      sx={sharedSx}
    >
      {logoLayers}
    </Box>
  );
};
